import React from "react";

import { faCompress } from "@fortawesome/pro-regular-svg-icons";
import { Provider, Root, Trigger } from "@radix-ui/react-tooltip";
import {
  Handle,
  NodeProps,
  NodeTypes,
  Position,
  Node as ReactFlowNode,
} from "@xyflow/react";
import { observer } from "mobx-react-lite";
import { twJoin } from "tailwind-merge";

import { useProviderManifest } from "src/api/connectApi/queries";
import { useCommentsSummary } from "src/comments/queries";
import { DEFAULT_UNTITLED_GROUP_NAME } from "src/constants/DefaultValues";
import {
  AgentNode as AgentNodeType,
  AiNodeV2 as AiNodeV2Type,
  AssignmentNode as AssignemntNodeType,
  BeMappedNode,
  CustomConnectionNode as CustomConnectionNodeType,
  DatabaseConnectionNode as DatabaseConnectionNodeType,
  DecisionTableNode as DecisionTableNodeType,
  GroupNode as GroupNodeType,
  GroupSeparatorNode,
  InboundWebhookConnectionNode as InboundWebhookConnectionNodeType,
  IntegrationNode as IntegrationNodeType,
  ManifestConnectionNode as ManifestConnectionNodeType,
  MatrixTableNode as MatrixTableNodeType,
  MLNode as MLNodeType,
  NodeBase,
  NodeRunStateV2,
  RuleNodeV2 as RuleNodeV2Type,
  ScorecardNode as ScoreCardNodeType,
  SimpleNode as SimpleNodeType,
  SplitNodeV2 as SplitNodeV2Type,
} from "src/constants/NodeDataTypes";
import { getNodeTitle } from "src/constants/NodeProperties";
import { NodeTypeIcon as getNodeIcon } from "src/constants/NodeTypeIcons";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { Icon } from "src/design-system/Icon";
import { useSelectedResultsRowIndex } from "src/flowContainer/AuthoringUIContext";
import {
  BaseNode,
  BaseNodeProps,
  handleClassName,
  renderHandles,
} from "src/flowGraph/BaseNode";
import { BranchNode } from "src/flowGraph/BranchNode";
import { CommentCount } from "src/flowGraph/CommentCount";
import { GroupNodeRunIcon, NodeRunIconV2 } from "src/flowGraph/NodeRunIconV2";
import { GroupControls } from "src/flowGraph/nodeControls/GroupControls";
import { NodeControls } from "src/flowGraph/nodeControls/NodeControls";
import {
  JobRunResultOverviewDonut,
  ResultOverviewDonut,
} from "src/flowGraph/ResultOverviewDonut";
import { useBranchAnalytics } from "src/flowGraph/useBranchAnalytics";
import {
  useJobRunCountsForNode,
  useJobRunMetrics,
  useOutputNodeJobRunCounts,
} from "src/flowGraph/useJobRunCounts";
import { getCreatableNodes } from "src/nodeAdding/PasteNodesCard";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { AuthorPageParams } from "src/router/urls";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import { useNodeValidations } from "src/store/NodeValidations";
import {
  useFlowOuput,
  useNodeRunState,
  useNodeRunStates,
} from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";
import { pluralize } from "src/utils/stringUtils";
import { useOpenOnHover } from "src/utils/useOpenOnHover";
import { useParamsDecode } from "src/utils/useParamsDecode";

type InputOutputBaseDivProps = {
  selected: boolean;
  children: React.ReactNode;
  id: string;
  runStateV2: NodeRunStateV2 | undefined;
} & (
  | {
      nodeType: NODE_TYPE.INPUT_NODE;
    }
  | { nodeType: NODE_TYPE.OUTPUT_NODE; flowOutput?: NodeRunStateV2 }
);

type SimpleNodeProps = Pick<
  BaseNodeProps,
  "icon" | "title" | "selected" | "label" | "badge"
> & {
  id: string;
  runStateV2?: NodeRunStateV2;
  withoutControls?: boolean;
};

const SimpleNode: React.FC<SimpleNodeProps> = ({
  badge,
  icon,
  title,
  id,
  label,
  selected,
  runStateV2,
  withoutControls = false,
}) => {
  const { isMultiselecting } = useGraphStore();
  const jobRunCounts = useJobRunCountsForNode(id);
  const isErrored =
    runStateV2?.type === "historical-error" ||
    runStateV2?.type === "test-error";
  const { version_id } = useParamsDecode<AuthorPageParams>();
  const { data: commentCount } = useCommentsSummary(version_id, id);
  const isInvalid = useNodeValidations((state) => state.isInvalid(id));

  const {
    data: { nodesGrayedOut },
  } = useNodeHighlighting();
  const grayedOut = Boolean(nodesGrayedOut[id]);

  const selectedRowIndex = useSelectedResultsRowIndex();

  let integrationLatencyData;
  const nodeExecutionMetadata =
    runStateV2 && "nodeExecutionMetadata" in runStateV2
      ? runStateV2.nodeExecutionMetadata?.[id]
      : undefined;
  if (
    nodeExecutionMetadata?.data_source === "mock_data" ||
    nodeExecutionMetadata?.data_source === "empty_response"
  ) {
    integrationLatencyData = {
      duration: 0,
      integrationStatus: nodeExecutionMetadata.data_source,
      isErrored: false,
      strippedDataObjectSizeBytes:
        nodeExecutionMetadata.after_execution_stripped_data_object_size_bytes,
    };
  } else {
    integrationLatencyData =
      runStateV2?.type === "historical-data" ||
      runStateV2?.type === "historical-error"
        ? runStateV2.integrationLatencyData
        : undefined;
  }
  return (
    <BaseNode
      badge={badge}
      commentCount={
        commentCount ? <CommentCount count={commentCount} /> : undefined
      }
      grayedOut={grayedOut}
      icon={icon}
      id={id}
      integrationLatencyData={integrationLatencyData}
      isErrored={isErrored}
      isInvalid={isInvalid}
      label={label}
      renderControls={
        !withoutControls
          ? ({ onMouseEnter, onMouseLeave, close }) => (
              <>
                {!isMultiselecting && (
                  <NodeControls
                    close={close}
                    id={id}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                  />
                )}
              </>
            )
          : undefined
      }
      runIcon={
        selectedRowIndex === null ? (
          <NodeRunIconV2 jobRunCounts={jobRunCounts} runStateV2={runStateV2} />
        ) : undefined
      }
      selected={selected}
      title={title}
    />
  );
};

const InputOutputBaseDiv: React.FC<InputOutputBaseDivProps> = ({
  selected,
  children,
  runStateV2,
  id,
  ...props
}) => {
  const jobRunCounts = useJobRunCountsForNode(id);
  const outputNodeCounts = useOutputNodeJobRunCounts();
  const { version_id } = useParamsDecode<AuthorPageParams>();
  const { data: commentCount } = useCommentsSummary(version_id, id);
  const {
    data: { nodesGrayedOut },
  } = useNodeHighlighting();
  const grayedOut = Boolean(nodesGrayedOut[id]);

  const selectedRowIndex = useSelectedResultsRowIndex();
  return (
    <>
      <div
        className={twJoin(
          "inline-flex h-11 min-w-[85px] items-center justify-center truncate rounded-3xl bg-white px-4 font-inter-semibold-13px shadow-base hover:cursor-pointer",
          grayedOut && "text-gray-500",
          !grayedOut && "text-gray-800",
          (runStateV2?.type === "historical-error" ||
            runStateV2?.type === "test-error") &&
            selected &&
            "border border-red-600",
          runStateV2?.type !== "historical-error" &&
            runStateV2?.type !== "test-error" &&
            selected &&
            "border border-indigo-400 outline-none ring-2 ring-indigo-500/25",
        )}
        data-loc={
          props.nodeType === NODE_TYPE.INPUT_NODE ? "input-node" : "output-node"
        }
      >
        {children}
        <div
          className={twJoin(
            "inline-flex items-center justify-center",
            grayedOut && "brightness-[1.02] grayscale",
          )}
        >
          {commentCount ? (
            <div className="ml-2">
              <CommentCount count={commentCount} />
            </div>
          ) : undefined}
          {props.nodeType === NODE_TYPE.INPUT_NODE &&
            selectedRowIndex === null && (
              <NodeRunIconV2
                jobRunCounts={jobRunCounts}
                runStateV2={runStateV2}
              />
            )}
          {props.nodeType === NODE_TYPE.OUTPUT_NODE &&
            selectedRowIndex === null &&
            (props.flowOutput?.type === "test-run" ? (
              <ResultOverviewDonut
                nodeTestResult={props.flowOutput.testResult}
              />
            ) : outputNodeCounts ? (
              <JobRunResultOverviewDonut
                failed={outputNodeCounts.failed}
                ignored={outputNodeCounts.ignored}
                success={outputNodeCounts.success}
              />
            ) : (
              <NodeRunIconV2
                jobRunCounts={jobRunCounts}
                runStateV2={runStateV2}
              />
            ))}
        </div>
      </div>
      {props.nodeType === NODE_TYPE.INPUT_NODE && (
        <Handle
          className={handleClassName}
          isConnectable={false}
          position={Position.Bottom}
          type="source"
        />
      )}
      {props.nodeType === NODE_TYPE.OUTPUT_NODE && (
        <Handle
          className={handleClassName}
          isConnectable={false}
          position={Position.Top}
          type="target"
        />
      )}
    </>
  );
};

const InputNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <InputOutputBaseDiv
      id={id}
      nodeType={NODE_TYPE.INPUT_NODE}
      runStateV2={runState}
      selected={selected}
    >
      <div>Input</div>
      {/* Needed because it has side effects to mobx. Ticket to remove this: AUTH-7100 */}
      <Provider>
        <Root>
          <Trigger />
        </Root>
      </Provider>
    </InputOutputBaseDiv>
  );
};

const OutputNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  const flowOutput = useFlowOuput();
  return (
    <InputOutputBaseDiv
      flowOutput={flowOutput}
      id={id}
      nodeType={NODE_TYPE.OUTPUT_NODE}
      runStateV2={runState}
      selected={selected}
    >
      <div>Output</div>
    </InputOutputBaseDiv>
  );
};

const AiNodeV2: React.FC<NodeProps<AiNodeV2Type>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.AI_NODE_V2 })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.AI_NODE_V2 })}
    />
  );
};

const AgentNode: React.FC<NodeProps<AgentNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.AGENT_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.AGENT_NODE })}
    />
  );
};

const CodeNode: React.FC<NodeProps<SimpleNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.CODE_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.CODE_NODE })}
    />
  );
};

const SplitNodeV2: React.FC<NodeProps<SplitNodeV2Type>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.SPLIT_NODE_V2 })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.SPLIT_NODE_V2 })}
    />
  );
};

const SplitBranchNode: React.FC<NodeProps<SimpleNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const {
    data: { nodesGrayedOut },
  } = useNodeHighlighting();
  const analytics = useBranchAnalytics(id);

  return (
    <BranchNode
      analytics={analytics}
      grayedOut={Boolean(nodesGrayedOut[id])}
      label={data.label}
      selected={selected}
    />
  );
};

/**
 * The split merge node has the minumum width and height react flow supports (1px)
 */
const SplitMergeNode: React.FC<NodeProps<SimpleNodeType>> = observer(() => {
  return (
    <>
      <div
        className="nodrag h-[1px] w-[1px] items-center justify-center opacity-0"
        data-loc="split-merge"
      />
      {renderHandles({ sourceHandleClassName: "-bottom-[2px]" })}
    </>
  );
});

const RuleNodeV2: React.FC<NodeProps<RuleNodeV2Type>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.RULE_NODE_V2 })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.RULE_NODE_V2 })}
    />
  );
};

const IntegrationNode: React.FC<NodeProps<IntegrationNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.INTEGRATION_NODE,
        provider: data.providerResource.provider,
        resource: data.providerResource.resource,
        size: "lg",
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({
        nodeType: NODE_TYPE.INTEGRATION_NODE,
        provider: data.providerResource.provider,
      })}
    />
  );
};

const ManifestConnectionNode: React.FC<
  NodeProps<ManifestConnectionNodeType>
> = ({ data, selected, id }) => {
  const runState = useNodeRunState(id);
  const { workspace } = useWorkspaceContext();
  const baseUrl = workspace.base_url;
  const provider = data.providerResource.provider;
  const manifestVersion = data.providerResource.manifest_version;
  const manifest = useProviderManifest(baseUrl, provider, manifestVersion).data;
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.MANIFEST_CONNECTION_NODE,
        provider: provider,
        size: "lg",
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={manifest?.display_name || provider}
    />
  );
};

const MLNode: React.FC<NodeProps<MLNodeType>> = ({ data, selected, id }) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.ML_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.ML_NODE })}
    />
  );
};

const CustomConnectionNode: React.FC<NodeProps<CustomConnectionNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.CUSTOM_CONNECTION_NODE,
        provider: "custom",
        mediaKey: data.mediaKey,
        size: "lg",
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.CUSTOM_CONNECTION_NODE })}
    />
  );
};

const InboundWebhookConnectionNode: React.FC<
  NodeProps<InboundWebhookConnectionNodeType>
> = ({ data, selected, id }) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.WEBHOOK_CONNECTION_NODE,
        provider: "webhook",
        mediaKey: data.mediaKey,
        size: "lg",
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.WEBHOOK_CONNECTION_NODE })}
    />
  );
};

const DatabaseConnectionNode: React.FC<
  NodeProps<DatabaseConnectionNodeType>
> = ({ data, selected, id }) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
        provider: data.providerResource.provider,
        resource: data.providerResource.resource,
        size: "lg",
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({
        nodeType: NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
        provider: data.providerResource.provider,
      })}
    />
  );
};

const MatrixTableNode: React.FC<NodeProps<MatrixTableNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);

  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.MATRIX_TABLE_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.MATRIX_TABLE_NODE })}
    />
  );
};

const DecisionTableNode: React.FC<NodeProps<DecisionTableNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.DECISION_TABLE_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.DECISION_TABLE_NODE })}
    />
  );
};

const AssignmentNode: React.FC<NodeProps<AssignemntNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.ASSIGNMENT_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.ASSIGNMENT_NODE })}
    />
  );
};

const ScorecardNode: React.FC<NodeProps<ScoreCardNodeType>> = ({
  data,
  selected,
  id,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.SCORECARD_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.SCORECARD_NODE })}
    />
  );
};

export const GroupNode: React.FC<NodeProps<GroupNodeType>> = observer(
  ({ data, selected, id }) => {
    const runStates = useNodeRunStates();
    const { toggleGroupNode, nodesArray, nodes, isMultiselecting, groups } =
      useGraphStore();
    const { isOpen, setIsOpen, onMouseEnter, onMouseLeave } = useOpenOnHover<
      HTMLDivElement,
      HTMLDivElement
    >(300);
    const childNodes = nodesArray.filter((node) =>
      data.childrenIDs.includes(node.id),
    );
    const { version_id } = useParamsDecode<AuthorPageParams>();
    const { data: commentCount } = useCommentsSummary(
      version_id,
      childNodes.map((n) => n.id),
    );
    const {
      data: { groupsGrayedOut },
    } = useNodeHighlighting();
    const grayedOut = Boolean(groupsGrayedOut[id]);
    const selectedRowIndex = useSelectedResultsRowIndex();

    const metrics = useJobRunMetrics();
    const allJobRunCounts = metrics?.data.status_counts;

    if (data.uiState.expanded) {
      const groupNodeStyle = {
        minWidth: groups.get(id)?.width ?? undefined,
        minHeight: groups.get(id)?.height ?? undefined,
      };
      return (
        <div
          className="relative rounded-lg border border-gray-200 bg-gray-200 bg-opacity-30 px-3 pt-4.5"
          style={groupNodeStyle}
        >
          <div
            className={twJoin(
              "flex items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border-gray-200 bg-white px-2 py-1 font-inter-semibold-13px shadow-sm",
              grayedOut ? "text-gray-500" : "text-gray-800",
              grayedOut ? "shadow-base" : "shadow-lg",
            )}
            data-loc={data.label}
            onClick={() => toggleGroupNode(id)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <div className={twJoin(grayedOut && "brightness-[1.02] grayscale")}>
              {getNodeIcon({
                nodeType: NODE_TYPE.GROUP_NODE,
                groupId: id,
                size: "xs",
              })}
            </div>
            <div className="flex flex-grow items-center justify-center">
              <span className="max-w-44 truncate text-center">
                {data.label ? data.label : DEFAULT_UNTITLED_GROUP_NAME}
              </span>
            </div>
            <Icon dataLoc="toggle-group" icon={faCompress} size="2xs" />
          </div>
          {isOpen && !isMultiselecting && (
            <GroupControls
              close={() => setIsOpen(false)}
              id={id}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          )}
        </div>
      );
    }

    const childNodeRunState = data.childrenIDs.map((childId) =>
      runStates.get(childId),
    );
    const nodeHasJobRunFailures = data.childrenIDs.some(
      (childId) =>
        (allJobRunCounts?.[childId]?.failed ?? 0) > 0 ||
        (allJobRunCounts?.[childId]?.ignored ?? 0) > 0,
    );
    const nodeHasJobRunCounts = data.childrenIDs.some(
      (childId) => allJobRunCounts?.[childId] != null,
    );
    return (
      <div data-loc="toggle-group" onClick={() => toggleGroupNode(id)}>
        <BaseNode
          commentCount={
            commentCount ? <CommentCount count={commentCount} /> : undefined
          }
          defaultName={DEFAULT_UNTITLED_GROUP_NAME}
          grayedOut={grayedOut}
          icon={getNodeIcon({ nodeType: NODE_TYPE.GROUP_NODE, groupId: id })}
          id={id}
          isErrored={false}
          label={data.label}
          renderControls={({ onMouseEnter, onMouseLeave, close }) => (
            <>
              {!isMultiselecting && (
                <GroupControls
                  close={close}
                  id={id}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              )}
            </>
          )}
          runIcon={
            selectedRowIndex === null ? (
              <GroupNodeRunIcon
                nodeHasJobRunCounts={nodeHasJobRunCounts}
                nodeHasJobRunFailures={nodeHasJobRunFailures}
                runStates={childNodeRunState}
              />
            ) : undefined
          }
          selected={selected}
          title={pluralize(
            getCreatableNodes(
              data.childrenIDs
                .map((childId) => nodes.get(childId))
                .filter((n) => n !== undefined) as BeMappedNode[],
            ).length,
            "node",
          )}
          withUnderlay
        />
      </div>
    );
  },
);

/**
 * A transparent node to keep space for the cosmetic node rendered by an open group node.
 */
const GroupPreSeparatorNode: React.FC<NodeProps<GroupSeparatorNode>> = () => {
  return (
    <>
      <div className="h-[31px] w-1 bg-transparent" />
      {renderHandles()}
    </>
  );
};

const GroupPostSeparatorNode: React.FC<NodeProps<GroupSeparatorNode>> = () => {
  return (
    <>
      <div className="nodrag flex h-[19px] w-1 items-center justify-center" />
      {renderHandles()}
    </>
  );
};

const FlowNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.FLOW_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.FLOW_NODE })}
    />
  );
};

const LoopNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({
        nodeType: NODE_TYPE.LOOP_NODE,
      })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.LOOP_NODE })}
    />
  );
};

const ManualReviewNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.REVIEW_CONNECTION_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.REVIEW_CONNECTION_NODE })}
    />
  );
};

const CaseCreationNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.CASE_CREATION_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.CASE_CREATION_NODE })}
    />
  );
};

const CaseUpdateNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.CASE_UPDATE_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.CASE_UPDATE_NODE })}
    />
  );
};

const ReadEntityNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.READ_ENTITY_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.READ_ENTITY_NODE })}
    />
  );
};

const WriteEntityNode: React.FC<NodeProps<ReactFlowNode<NodeBase>>> = ({
  id,
  data,
  selected,
}) => {
  const runState = useNodeRunState(id);
  return (
    <SimpleNode
      icon={getNodeIcon({ nodeType: NODE_TYPE.WRITE_ENTITY_NODE })}
      id={id}
      label={data.label}
      runStateV2={runState}
      selected={selected}
      title={getNodeTitle({ nodeType: NODE_TYPE.WRITE_ENTITY_NODE })}
    />
  );
};

export const NODES: NodeTypes = {
  ai_node_v2: AiNodeV2,
  agent_node: AgentNode,
  input_node: InputNode,
  code_node: CodeNode,
  output_node: OutputNode,
  rule_node_v2: RuleNodeV2,
  split_node_v2: SplitNodeV2,
  split_branch_node_v2: SplitBranchNode,
  split_merge_node: SplitMergeNode,
  data_integration_node: IntegrationNode,
  manifest_connection_node: ManifestConnectionNode,
  ml_node: MLNode,
  decision_table_node: DecisionTableNode,
  matrix_table_node: MatrixTableNode,
  assignment_node: AssignmentNode,
  custom_connection_node: CustomConnectionNode,
  webhook_connection_node: InboundWebhookConnectionNode,
  sql_database_connection_node: DatabaseConnectionNode,
  scorecard_node: ScorecardNode,
  group_node: GroupNode,
  group_pre_separator_node: GroupPreSeparatorNode,
  group_post_separator_node: GroupPostSeparatorNode,
  flow_node: FlowNode,
  review_connection_node: ManualReviewNode,
  loop_node: LoopNode,
  case_creation_node: CaseCreationNode,
  case_update_node: CaseUpdateNode,
  read_entity_node: ReadEntityNode,
  write_entity_node: WriteEntityNode,
};
